import { Capacitor, registerPlugin } from '@capacitor/core';

// The native class is registered by MainActivity only in the Android build.
// Keeping the proxy here makes every web import a harmless no-op.
const AthenaVoiceNative = registerPlugin('AthenaVoice');

const noop = () => {};

function normalizePermissionState(value) {
  return typeof value === 'string' ? value.toLowerCase() : 'unknown';
}

export function isNativeVoicePluginAvailable() {
  try {
    return Capacitor.isNativePlatform()
      && Capacitor.getPlatform() === 'android'
      && Capacitor.isPluginAvailable('AthenaVoice');
  } catch {
    return false;
  }
}

export function getNativeVoicePlugin() {
  return isNativeVoicePluginAvailable() ? AthenaVoiceNative : null;
}

/**
 * Reads the Android runtime state independently from the broader voice
 * capability snapshot. This prevents a stale "available" capability from
 * being treated as a granted microphone permission after the user responds
 * to the native dialog.
 */
export async function getNativeMicrophonePermission() {
  const plugin = getNativeVoicePlugin();
  if (!plugin) return { available: false, granted: false, state: 'unavailable' };

  try {
    const result = await plugin.checkPermissions();
    const state = normalizePermissionState(result?.microphone);
    return { available: true, granted: state === 'granted', state };
  } catch {
    return { available: true, granted: false, state: 'unknown', error: 'permission_check_failed' };
  }
}

/**
 * Requests only the microphone alias. The recognizer is started in a later
 * native call, after the promise returns, so the activity lifecycle caused by
 * the Android permission sheet cannot discard the just-created session.
 */
export async function requestNativeMicrophonePermission() {
  const plugin = getNativeVoicePlugin();
  if (!plugin) return { available: false, granted: false, state: 'unavailable' };

  const current = await getNativeMicrophonePermission();
  if (current.granted) return current;

  try {
    const result = await plugin.requestPermissions({ permissions: ['microphone'] });
    const state = normalizePermissionState(result?.microphone);
    return { available: true, granted: state === 'granted', state };
  } catch {
    return { available: true, granted: false, state: 'unknown', error: 'permission_request_failed' };
  }
}

export async function addNativeVoiceListener(eventName, listener) {
  const plugin = getNativeVoicePlugin();
  if (!plugin || typeof listener !== 'function') return noop;

  try {
    const handle = await plugin.addListener(eventName, listener);
    return () => {
      try {
        const removal = handle?.remove?.();
        if (removal?.catch) removal.catch(noop);
      } catch {
        // WebView destruction can invalidate a listener before React unmounts.
      }
    };
  } catch {
    return noop;
  }
}

export async function getNativeVoiceCapabilities() {
  const plugin = getNativeVoicePlugin();
  if (!plugin) {
    return {
      native: false,
      microphone: false,
      microphoneGranted: false,
      stt: false,
      continuous: false,
      bargeIn: false,
      tts: false,
      permission: 'unavailable',
      provider: 'unavailable',
    };
  }

  try {
    return await plugin.getCapabilities();
  } catch {
    return {
      native: true,
      microphone: true,
      microphoneGranted: false,
      stt: false,
      continuous: false,
      bargeIn: false,
      tts: true,
      permission: 'unknown',
      provider: 'android_speech_recognizer',
    };
  }
}
